import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Loader2, AlertCircle, Layers, Receipt, Download } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';

const API_URL = 'https://api.glowytics.com';

const PLATFORMS = [
  { key: 'GOOGLE_ADS', label: 'Google Ads', url: '/api/google-ads/live/campaign-insights', color: '#4285F4' },
  { key: 'META_ADS', label: 'Meta Ads', url: '/api/meta-ads/live/campaign-insights', color: '#0866FF' },
  { key: 'TIKTOK_ADS', label: 'TikTok Ads', url: '/api/tiktok-ads/live/campaign-insights', color: '#111111' },
];

const DATE_RANGES = [
  { value: 'last7', label: 'Son 7 gün' },
  { value: 'last30', label: 'Son 30 gün' },
  { value: 'last90', label: 'Son 90 gün' },
  { value: 'thisMonth', label: 'Bu ay' },
  { value: 'lastMonth', label: 'Geçen ay' },
  { value: 'custom', label: 'Özel aralık' },
];

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtMoney = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => fmtNum(n, 2) + '%';
const roasStr = (n) => fmtNum(n, 2) + 'x';

// ── Glowy Hakediş & Faturalama oranları ──────────────────────────────────────
const GOOGLE_FEE = 0.045; // %4.5 Google platform fee (yalnızca Google Ads)
const STOPAJ = 0.15;      // %15 stopaj — net tabanın 0.85'e bölünmesiyle "gross-up"
const KDV = 0.20;         // %20 KDV
const GLOWY_RATE = 0.10;  // Glowy hakediş = HAM harcama × %10 (fee/stopaj/KDV'den bağımsız)

// Tek bir platform satırının ham harcamadan tüm faturalama kalemlerini türetir.
function computeBilling(rawSpend, platformKey) {
  const raw = Number(rawSpend || 0);
  const googleFeeDahil = platformKey === 'GOOGLE_ADS' ? raw * (1 + GOOGLE_FEE) : null;
  const base = googleFeeDahil != null ? googleFeeDahil : raw; // stopaj tabanı
  const stopajDahil = base / (1 - STOPAJ);
  const stopaj = stopajDahil - base;
  const kdvTutar = stopajDahil * KDV;
  const kdvDahil = stopajDahil * (1 + KDV);
  const hakedis = raw * GLOWY_RATE;
  return { raw, googleFeeDahil, base, stopajDahil, stopaj, kdvTutar, kdvDahil, hakedis };
}

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Seçili dateRange'i gerçek {start,end} tarihlerine çözer (backend mantığıyla aynı)
function resolveRange(dateRange, startDate, endDate) {
  const now = new Date();
  let start, end;
  switch (dateRange) {
    case 'last7':  end = now; start = new Date(now); start.setDate(start.getDate() - 7); break;
    case 'last30': end = now; start = new Date(now); start.setDate(start.getDate() - 30); break;
    case 'last90': end = now; start = new Date(now); start.setDate(start.getDate() - 90); break;
    case 'thisMonth': start = new Date(now.getFullYear(), now.getMonth(), 1); end = now; break;
    case 'lastMonth': start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
    case 'custom':
      if (!startDate || !endDate) return {};
      start = new Date(startDate); end = new Date(endDate); break;
    default: end = now; start = new Date(now); start.setDate(start.getDate() - 30);
  }
  return { start, end };
}

// Aralığı takvim aylarına böler; ilk/son ay aralığa göre kırpılır (kısmi olabilir)
function monthsInRange(start, end) {
  const out = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endMonth) {
    const mStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
    const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const cs = mStart < start ? start : mStart;
    const ce = mEnd > end ? end : mEnd;
    const partial = cs.getDate() !== 1 || ce.getTime() !== mEnd.getTime();
    out.push({
      key: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`,
      label: `${TR_MONTHS[cur.getMonth()]} ${cur.getFullYear()}`,
      start: ymd(cs), end: ymd(ce), partial,
    });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return out;
}

function sumBillingRows(rows) {
  return rows.reduce((t, r) => ({
    raw: t.raw + r.raw,
    googleFeeDahil: t.googleFeeDahil + (r.googleFeeDahil ?? r.raw),
    stopaj: t.stopaj + r.stopaj,
    stopajDahil: t.stopajDahil + r.stopajDahil,
    kdvTutar: t.kdvTutar + r.kdvTutar,
    kdvDahil: t.kdvDahil + r.kdvDahil,
    hakedis: t.hakedis + r.hakedis,
  }), { raw: 0, googleFeeDahil: 0, stopaj: 0, stopajDahil: 0, kdvTutar: 0, kdvDahil: 0, hakedis: 0 });
}

export default function HupalupaGoReport() {
  const { selectedBrand } = useAuth();
  const [dateRange, setDateRange] = useState('last30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [nameContains, setNameContains] = useState('go');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // { GOOGLE_ADS: {...}, ... }
  const [globalErr, setGlobalErr] = useState(null);
  const [monthlyBilling, setMonthlyBilling] = useState(null); // [{ key, label, partial, rows, totals }]
  const [billingLoading, setBillingLoading] = useState(false);

  const load = useCallback(async () => {
    if (!selectedBrand?.id) { setGlobalErr('Önce bir marka seçin.'); return; }
    if (dateRange === 'custom' && (!startDate || !endDate)) { setGlobalErr('Özel aralık için başlangıç ve bitiş tarihi girin.'); return; }
    setLoading(true); setGlobalErr(null);
    const params = { dateRange, nameContains: nameContains.trim() };
    if (dateRange === 'custom') { params.startDate = startDate; params.endDate = endDate; }
    const headers = { 'X-Brand-ID': selectedBrand.id };

    const settled = await Promise.allSettled(
      PLATFORMS.map((p) => axios.get(`${API_URL}${p.url}`, { params, headers, withCredentials: true, timeout: 120000 }))
    );
    const next = {};
    settled.forEach((s, i) => {
      const key = PLATFORMS[i].key;
      if (s.status === 'fulfilled') {
        next[key] = { status: 'ok', data: s.value.data };
      } else {
        const code = s.reason?.response?.status;
        const msg = s.reason?.response?.data?.error || s.reason?.message;
        next[key] = { status: code === 404 ? 'noconn' : 'error', error: msg };
      }
    });
    setResults(next);
    setLoading(false);
  }, [selectedBrand, dateRange, startDate, endDate, nameContains]);

  // Aylık hakediş — aralığı aylara bölüp her ay × platform için ayrı canlı istek
  const loadMonthlyBilling = useCallback(async () => {
    if (!selectedBrand?.id) return;
    const { start, end } = resolveRange(dateRange, startDate, endDate);
    if (!start || !end) return;
    const months = monthsInRange(start, end);
    if (months.length === 0) return;
    setBillingLoading(true);
    const headers = { 'X-Brand-ID': selectedBrand.id };
    const nc = nameContains.trim();
    const blocks = await Promise.all(months.map(async (m) => {
      const params = { dateRange: 'custom', startDate: m.start, endDate: m.end, nameContains: nc };
      const settled = await Promise.allSettled(
        PLATFORMS.map((p) => axios.get(`${API_URL}${p.url}`, { params, headers, withCredentials: true, timeout: 120000 }))
      );
      const rows = [];
      settled.forEach((s, i) => {
        if (s.status === 'fulfilled') {
          const raw = Number(s.value.data?.totals?.spend || 0);
          if (raw > 0) rows.push({ platform: PLATFORMS[i], ...computeBilling(raw, PLATFORMS[i].key) });
        }
      });
      return { ...m, rows, totals: sumBillingRows(rows) };
    }));
    setMonthlyBilling(blocks.filter((b) => b.rows.length > 0));
    setBillingLoading(false);
  }, [selectedBrand, dateRange, startDate, endDate, nameContains]);

  useEffect(() => { load(); loadMonthlyBilling(); /* ilk yüklemede çalıştır */ }, [load, loadMonthlyBilling]);

  // Birleşik toplamlar (başarılı platformlar)
  const combined = React.useMemo(() => {
    if (!results) return null;
    const t = { spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 };
    let any = false;
    for (const p of PLATFORMS) {
      const r = results[p.key];
      if (r?.status === 'ok' && r.data?.totals) {
        any = true;
        const tot = r.data.totals;
        t.spend += Number(tot.spend || 0);
        t.impressions += Number(tot.impressions || 0);
        t.clicks += Number(tot.clicks || 0);
        t.conversions += Number(tot.conversions || 0);
        t.revenue += Number(tot.revenue || 0);
      }
    }
    return any ? t : null;
  }, [results]);

  // Tüm ayların grand total'ı
  const billingGrand = React.useMemo(() => {
    if (!monthlyBilling || monthlyBilling.length === 0) return null;
    return sumBillingRows(monthlyBilling.map((m) => m.totals));
  }, [monthlyBilling]);

  const exportBillingXlsx = useCallback(() => {
    if (!monthlyBilling || monthlyBilling.length === 0) return;
    const brand = selectedBrand?.name || 'Marka';
    const fileBrand = brand.replace(/[^\w-]+/g, '_');
    const MONEY = '#,##0';
    const COLS = 9; // A..I

    // ── Stiller ──────────────────────────────────────────────
    const thin = { style: 'thin', color: { rgb: 'CBD5E1' } };
    const grid = { top: thin, bottom: thin, left: thin, right: thin };
    const headerS = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 }, fill: { patternType: 'solid', fgColor: { rgb: '1F2937' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: grid };
    const titleS = { font: { bold: true, sz: 14, color: { rgb: '111827' } } };
    const subS = { font: { sz: 9, color: { rgb: '6B7280' } } };
    const bannerS = { font: { bold: true, sz: 11, color: { rgb: '111827' } }, fill: { patternType: 'solid', fgColor: { rgb: 'E5E7EB' } }, border: grid, alignment: { horizontal: 'left' } };
    const numS = { border: grid, alignment: { horizontal: 'right' } };
    const txtS = { border: grid, alignment: { horizontal: 'left' } };
    const totS = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } }, border: grid, alignment: { horizontal: 'right' } };
    const totTxtS = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } }, border: grid, alignment: { horizontal: 'left' } };
    const grandS = { font: { bold: true, color: { rgb: '065F46' } }, fill: { patternType: 'solid', fgColor: { rgb: 'D1FAE5' } }, border: grid, alignment: { horizontal: 'right' } };
    const grandTxtS = { font: { bold: true, color: { rgb: '065F46' } }, fill: { patternType: 'solid', fgColor: { rgb: 'D1FAE5' } }, border: grid, alignment: { horizontal: 'left' } };
    const amber = { fill: { patternType: 'solid', fgColor: { rgb: 'FEF3C7' } } };

    // ── Hücre yapıcıları ─────────────────────────────────────
    const txt = (v, s) => ({ v: v ?? '', t: 's', s });
    const fml = (f, s) => ({ t: 'n', f, z: MONEY, s: { ...numS, ...(s || {}) } });
    const val = (v, s) => ({ t: 'n', v: Math.round(Number(v || 0)), z: MONEY, s: { ...numS, ...(s || {}) } });

    const rows = [];   // satır dizisi (hücre objesi dizileri)
    const merges = [];
    const mergeRow = () => merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: COLS - 1 } });

    // Başlık + alt başlık
    rows.push([txt(`Glowy Hakediş & Faturalama — ${brand}`, titleS)]); mergeRow();
    rows.push([txt(`Aylık · canlı harcamadan${nameContains ? ` · "${nameContains}" filtreli` : ''}`, subS)]); mergeRow();
    rows.push([]);

    // Tablo başlığı
    const header = ['Platform', 'Harcama', 'Google %4.5 Fee Dahil', 'Stopaj %15', 'Stopaj Dahil', 'KDV %20', 'KDV Tutarı', 'Total (KDV Dahil)', 'Glowy Hakediş'];
    rows.push(header.map((h) => txt(h, headerS)));

    const monthTotalRowNums = [];
    monthlyBilling.forEach((m) => {
      // Ay banner'ı (birleştirilmiş)
      rows.push([txt(`${m.label}  (${m.start} → ${m.end})${m.partial ? ' · kısmi ay' : ''}`, bannerS)]); mergeRow();

      const firstRow = rows.length + 1; // ilk platform satırının Excel satır no'su
      m.rows.forEach((r) => {
        const R = rows.length + 1; // bu satırın Excel no'su
        const isG = r.platform.key === 'GOOGLE_ADS';
        const baseRef = isG ? `C${R}` : `B${R}`; // stopaj tabanı: Google'da fee dahil, diğerlerinde ham
        rows.push([
          txt(r.platform.label, txtS),
          val(r.raw, amber),                                  // B Harcama (girdi)
          isG ? fml(`B${R}*1.045`) : txt('', numS),           // C Google %4.5 fee
          fml(`E${R}-${baseRef}`),                            // D Stopaj %15 (tutar)
          fml(`${baseRef}/0.85`),                             // E Stopaj Dahil
          fml(`E${R}*1.2`),                                   // F KDV %20
          fml(`E${R}*0.2`),                                   // G KDV Tutarı
          fml(`E${R}*1.2`),                                   // H Total (KDV Dahil)
          fml(`B${R}*0.1`, amber),                            // I Glowy Hakediş
        ]);
      });
      const lastRow = rows.length; // son platform satırı
      const TR = rows.length + 1;  // AY TOPLAM satırı
      const sum = (col) => `SUM(${col}${firstRow}:${col}${lastRow})`;
      rows.push([
        txt('AY TOPLAM', totTxtS),
        fml(sum('B'), totS), fml(sum('C'), totS), fml(sum('D'), totS), fml(sum('E'), totS),
        fml(sum('F'), totS), fml(sum('G'), totS), fml(sum('H'), totS), fml(sum('I'), totS),
      ]);
      monthTotalRowNums.push(TR);
      rows.push([]); // aylar arası boşluk
    });

    // GENEL TOPLAM (ay toplamlarının toplamı)
    if (monthlyBilling.length > 1) {
      const sumT = (col) => monthTotalRowNums.map((n) => `${col}${n}`).join('+');
      rows.push([
        txt(`GENEL TOPLAM (${monthlyBilling.length} ay)`, grandTxtS),
        fml(sumT('B'), grandS), fml(sumT('C'), grandS), fml(sumT('D'), grandS), fml(sumT('E'), grandS),
        fml(sumT('F'), grandS), fml(sumT('G'), grandS), fml(sumT('H'), grandS), fml(sumT('I'), grandS),
      ]);
    }

    // ── Sayfayı manuel kur (hücre objeleri + stiller korunur) ─
    const ws = {};
    let maxR = 0;
    rows.forEach((row, r) => {
      row.forEach((cell, c) => {
        if (cell == null) return;
        ws[XLSX.utils.encode_cell({ r, c })] = cell;
      });
      if (row.length) maxR = r;
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: COLS - 1 } });
    ws['!merges'] = merges;
    ws['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 13 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 15 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Glowy Hakediş');
    XLSX.writeFile(wb, `glowy-hakedis-${fileBrand}.xlsx`);
  }, [monthlyBilling, selectedBrand, nameContains]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Layers className="w-6 h-6 text-fuchsia-600" />
        <h1 className="text-2xl font-bold text-gray-900">Hupalupa GO — Çok Platform Rapor</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Google · Meta · TikTok — ismi "<strong>{nameContains || '…'}</strong>" geçen kampanyalar, <strong>canlı</strong> API'den
        {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
      </p>

      {/* Kontroller */}
      <div className="flex flex-wrap items-end gap-3 mb-6 print:hidden">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Tarih aralığı</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-fuchsia-500">
            {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {dateRange === 'custom' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Başlangıç</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-fuchsia-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Bitiş</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-fuchsia-500" />
            </div>
          </>
        )}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Kampanya adı içerir</label>
          <input value={nameContains} onChange={(e) => setNameContains(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28 outline-none focus:ring-2 focus:ring-fuchsia-500" placeholder="go" />
        </div>
        <button onClick={() => { load(); loadMonthlyBilling(); }} disabled={loading}
          className="flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {loading ? 'Çekiliyor…' : 'Yenile'}
        </button>
      </div>

      {globalErr && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {globalErr}
        </div>
      )}

      {loading && !results && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> 3 platformdan canlı veri çekiliyor…
        </div>
      )}

      {/* Birleşik özet */}
      {combined && (
        <div className="bg-gradient-to-r from-fuchsia-50 to-purple-50 border border-fuchsia-200 rounded-xl p-4 mb-6">
          <div className="text-xs font-bold uppercase text-fuchsia-700 mb-2">Toplam (Google + Meta + TikTok)</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Kpi label="Harcama" value={fmtMoney(combined.spend)} />
            <Kpi label="Gösterim" value={fmtInt(combined.impressions)} />
            <Kpi label="Tıklama" value={fmtInt(combined.clicks)} />
            <Kpi label="Dönüşüm" value={fmtInt(combined.conversions)} />
            <Kpi label="Gelir" value={fmtMoney(combined.revenue)} />
            <Kpi label="ROAS" value={combined.spend > 0 ? roasStr(combined.revenue / combined.spend) : '—'} />
          </div>
        </div>
      )}

      {/* Glowy Hakediş & Faturalama — aylık */}
      {(monthlyBilling || billingLoading) && (
        <BillingSection
          months={monthlyBilling}
          grand={billingGrand}
          loading={billingLoading}
          brandName={selectedBrand?.name}
          onExport={exportBillingXlsx}
          nameContains={nameContains}
        />
      )}

      {/* Platform bazlı */}
      {results && (
        <div className="space-y-6">
          {PLATFORMS.map((p) => (
            <PlatformSection key={p.key} platform={p} result={results[p.key]} totalSpend={combined?.spend} />
          ))}
        </div>
      )}
    </div>
  );
}

function BillingRows({ rows, totals, totalLabel = 'AY TOPLAM' }) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.platform.key} className="hover:bg-gray-50">
          <td className="px-3 py-2 font-medium text-gray-800">
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.platform.color }} />
              {r.platform.label}
            </span>
          </td>
          <td className="px-3 py-2 text-right tabular-nums bg-amber-50/60 font-medium">{fmtMoney(r.raw)}</td>
          <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.googleFeeDahil != null ? fmtMoney(r.googleFeeDahil) : '—'}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.stopaj)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.stopajDahil)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.kdvDahil)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.kdvTutar)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.kdvDahil)}</td>
          <td className="px-3 py-2 text-right tabular-nums bg-amber-50/60 font-bold text-emerald-700">{fmtMoney(r.hakedis)}</td>
        </tr>
      ))}
      <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold text-gray-900">
        <td className="px-3 py-2">{totalLabel}</td>
        <td className="px-3 py-2 text-right tabular-nums bg-amber-100/70">{fmtMoney(totals.raw)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-400">—</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.stopaj)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.stopajDahil)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.kdvDahil)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.kdvTutar)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.kdvDahil)}</td>
        <td className="px-3 py-2 text-right tabular-nums bg-amber-100/70 text-emerald-700">{fmtMoney(totals.hakedis)}</td>
      </tr>
    </>
  );
}

function BillingTableHead() {
  return (
    <thead className="text-gray-400 uppercase border-b border-gray-100 bg-gray-50">
      <tr>
        <th className="text-left px-3 py-2 font-semibold">Platform</th>
        <th className="text-right px-3 py-2 font-semibold bg-amber-50">Harcama</th>
        <th className="text-right px-3 py-2 font-semibold">Google %4.5 Fee Dahil</th>
        <th className="text-right px-3 py-2 font-semibold">Stopaj %15</th>
        <th className="text-right px-3 py-2 font-semibold">Stopaj Dahil</th>
        <th className="text-right px-3 py-2 font-semibold">KDV %20</th>
        <th className="text-right px-3 py-2 font-semibold">KDV Tutarı</th>
        <th className="text-right px-3 py-2 font-semibold">Total (KDV Dahil)</th>
        <th className="text-right px-3 py-2 font-semibold bg-amber-50">Glowy Hakediş</th>
      </tr>
    </thead>
  );
}

function BillingSection({ months, grand, loading, brandName, onExport, nameContains }) {
  const hasData = months && months.length > 0;
  return (
    <div className="bg-white rounded-xl border border-emerald-200 shadow-sm overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-emerald-100 bg-emerald-50/60 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-600" />
          <h2 className="text-base font-bold text-gray-900">Glowy Hakediş &amp; Faturalama <span className="text-xs font-normal text-gray-500">— aylık</span></h2>
          <span className="text-xs text-gray-500">· {brandName || 'marka'} · canlı harcamadan</span>
        </div>
        <button onClick={onExport} disabled={!hasData}
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg print:hidden">
          <Download className="w-3.5 h-3.5" /> Excel (.xlsx) indir
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-6 px-4 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Aylık hakediş hesaplanıyor…
        </div>
      )}

      {!loading && !hasData && (
        <div className="px-4 py-6 text-sm text-gray-400 text-center">Bu aralıkta harcama bulunamadı.</div>
      )}

      {hasData && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <BillingTableHead />
            {months.map((m) => (
              <tbody key={m.key} className="divide-y divide-gray-50 border-b border-gray-100">
                <tr className="bg-slate-50">
                  <td colSpan={9} className="px-4 py-2">
                    <span className="text-sm font-bold text-gray-800">{m.label}</span>
                    <span className="text-[11px] text-gray-400 ml-2">{m.start} → {m.end}{m.partial ? ' · kısmi ay' : ''}</span>
                  </td>
                </tr>
                <BillingRows rows={m.rows} totals={m.totals} totalLabel={`${m.label} TOPLAM`} />
              </tbody>
            ))}
            {grand && months.length > 1 && (
              <tfoot>
                <tr className="border-t-2 border-emerald-200 bg-emerald-50/40 font-bold text-gray-900">
                  <td className="px-3 py-2.5 text-emerald-800">GENEL TOPLAM ({months.length} ay)</td>
                  <td className="px-3 py-2.5 text-right tabular-nums bg-amber-100/70">{fmtMoney(grand.raw)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">—</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(grand.stopaj)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(grand.stopajDahil)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(grand.kdvDahil)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(grand.kdvTutar)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(grand.kdvDahil)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums bg-amber-100/70 text-emerald-700">{fmtMoney(grand.hakedis)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {hasData && (
        <div className="px-4 py-2.5 text-[11px] text-gray-500 border-t border-gray-100 leading-relaxed">
          <strong>Glowy hakediş = ham harcama × %10</strong> (fee/stopaj/KDV'den bağımsız). Stopaj %15 net tabanın 0.85'e bölünmesiyle (gross-up),
          Google %4.5 fee yalnızca Google Ads'te uygulanır, KDV %20. Her ay için ayrı canlı istek atılır; aralığın ilk/son ayı kısmi olabilir.
          Rakamlar {nameContains ? <> ismi "<strong>{nameContains}</strong>" geçen</> : ' tüm'} kampanyaların harcamasına dayanır —
          tam fatura için kampanya adı filtresini boş bırakın.
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-lg font-bold text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function PlatformSection({ platform, result, totalSpend }) {
  if (!result) return null;

  if (result.status === 'noconn') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 opacity-70">
        <PlatformHeader platform={platform} note="bağlantı yok" />
      </div>
    );
  }
  if (result.status === 'error') {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-4">
        <PlatformHeader platform={platform} note="hata" />
        <div className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {result.error}</div>
      </div>
    );
  }

  const { totals, campaigns = [] } = result.data || {};
  const share = totalSpend > 0 ? (Number(totals?.spend || 0) / totalSpend) * 100 : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden break-inside-avoid">
      <div className="px-4 py-3 border-b border-gray-100">
        <PlatformHeader platform={platform} note={`${campaigns.length} kampanya${share != null ? ` · harcamanın %${fmtNum(share, 0)}` : ''}`} />
        {totals && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
            <MiniKpi label="Harcama" value={fmtMoney(totals.spend)} />
            <MiniKpi label="Gösterim" value={fmtInt(totals.impressions)} />
            <MiniKpi label="Tıklama" value={fmtInt(totals.clicks)} />
            <MiniKpi label="Dönüşüm" value={fmtInt(totals.conversions)} />
            <MiniKpi label="Gelir" value={fmtMoney(totals.revenue)} />
            <MiniKpi label="ROAS" value={totals.spend > 0 ? roasStr(totals.revenue / totals.spend) : '—'} />
          </div>
        )}
      </div>

      {campaigns.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400 text-center">Bu aralıkta eşleşen kampanya yok.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-400 uppercase border-b border-gray-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Kampanya</th>
                <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                <th className="text-right px-3 py-2 font-semibold">CTR</th>
                <th className="text-right px-3 py-2 font-semibold">CPM</th>
                <th className="text-right px-3 py-2 font-semibold">Dönüşüm</th>
                <th className="text-right px-3 py-2 font-semibold">Gelir</th>
                <th className="text-right px-3 py-2 font-semibold">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {campaigns.map((c) => (
                <tr key={c.campaign_id || c.campaign_name} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-gray-700 max-w-[280px] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.spend)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.impressions)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.clicks)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(c.ctr)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.cpm)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.conversions)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.spend > 0 ? roasStr(c.roas) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlatformHeader({ platform, note }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: platform.color }} />
      <h2 className="text-base font-bold text-gray-900">{platform.label}</h2>
      {note && <span className="text-xs text-gray-400">· {note}</span>}
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-gray-400 uppercase">{label}</div>
      <div className="text-sm font-semibold text-gray-800 tabular-nums">{value}</div>
    </div>
  );
}
